import type { ReadConfig } from 'api-read-hook';
import type { HydraCollection } from '../../types';

import { useApiRead } from 'api-read-hook';
import { makeQueryString } from '../../util';
import * as MediaUpload from './MediaUpload';

export type V_ClientList = {
  '@id': string;
  '@type': 'User';
  user: {
    '@id': string;
    '@type': 'User';
    id: string;
    email: string;
    name: string | null;
    registeredAt: string;
    classifications: Array<{
      '@id': string;
      '@type': 'ClientClassification';
      id: string;
      name: string;
    }>;
  };
  programProgress: {
    '@id': string;
    '@type': 'ProgramProgress';
    status: string;
    currentWeekNumber: number | null;
  };
  subscriptionInfo: {
    '@id': string;
    '@type': 'SubscriptionInfo';
    isActive: boolean;
    status: string | null;
    source: string | null;
    licence: {
      '@id': string;
      '@type': 'TeamLicence';
      team: {
        '@id': string;
        '@type': 'OrganisationTeam';
        name: string;
        organisation: {
          '@id': string;
          '@type': 'Organisation';
          name: string;
        };
      };
      expiresAt: string | null;
      revokedAt: string | null;
    } | null;
    subscription: {
      '@id': string;
      '@type': 'Subscription';
      inactiveAt: string | null;
      stripeSubscriptionPlan: string | null;
      paymentOverdueAt: string | null;
      autoRenewCanceledAt: string | null;
    } | null;
  };
  appointmentHeld: number | null;
  appointmentScheduled: number | null;
};

export type V_ClinicianDetailAsAdmin = {
  '@id': string;
  '@type': 'User';
  email: string;
  timezone: string;
  name: string | null;
  clinicianProfile: {
    '@id': string;
    '@type': 'UserClinicianProfile';
    isActive: boolean;
    type: string;
    description: string;
    hasEditAccess: boolean;
    specialisation: string | null;
    appointmentDuration: number;
    appointmentBuffer: number;
  } | null;
  displayPicture: MediaUpload.BaseView | null;
  classifications: Array<string>;
  totalClients: number;
};

export type V_ClinicianListAsAdmin = {
  '@id': string;
  '@type': 'User';
  id: string;
  email: string;
  name: string | null;
  clinicianProfile: {
    '@id': string;
    '@type': 'UserClinicianProfile';
    type: string;
  } | null;
  displayPicture: MediaUpload.BaseView | null;
  totalClients: number;
};

export type V_ListAsAdmin = {
  '@id': string;
  '@type': 'User';
  user: {
    '@id': string;
    '@type': 'User';
    id: string;
    email: string;
    name: string | null;
    registeredAt: string;
  };
  programProgress: {
    '@id': string;
    '@type': 'ProgramProgress';
    status: string;
    currentWeekNumber: number | null;
  };
  /** Program the progress is for: the latest one, or the one filtered on. Null before any. */
  programTitle: string | null;
  subscriptionInfo: {
    '@id': string;
    '@type': 'SubscriptionInfo';
    isActive: boolean;
    status: string | null;
    source: string | null;
    licence: {
      '@id': string;
      '@type': 'TeamLicence';
      team: {
        '@id': string;
        '@type': 'OrganisationTeam';
        name: string;
        organisation: {
          '@id': string;
          '@type': 'Organisation';
          name: string;
        };
      };
      expiresAt: string | null;
      revokedAt: string | null;
    } | null;
    subscription: {
      '@id': string;
      '@type': 'Subscription';
      inactiveAt: string | null;
      stripeSubscriptionPlan: string | null;
      paymentOverdueAt: string | null;
      autoRenewCanceledAt: string | null;
      planDuration: string;
    } | null;
  };
  assignedCoachName: string | null;
  assignedPhysicianName: string | null;
  /** Has ever paid for a programme after their first one (renewal or re-purchase) */
  subscriptionRenewed: boolean;
  /** Has ever paid for Support Unlock */
  supportUnlockPurchased: boolean;
};

export type V_Me = {
  '@id': string;
  '@type': 'User';
  displayPicture: string | null;
};

export type W_Clinician = {
  email: string;
  name: string | null;
  clinicianProfile: {
    isActive: boolean;
    type: string;
    description: string;
    hasEditAccess: boolean;
    specialisation: string | null;
  } | null;
  displayPicture: string | null;
  classifications: Array<string>;
};

export type W_Me = {
  name: string | null;
  country: string | null;
  height: number | null;
  weight: number | null;
  consentMarketing: boolean | null;
  pregnancyIntent: string | null;
  recipePreferences: Array<string>;
  frequentSymptoms: Array<string>;
  contraceptions: Array<{
    type: string;
    currentlyUsing: boolean;
    usageLength: number;
    stoppedUsingAt: string | null;
    customSymptom: string | null;
    symptoms: Array<string>;
  }>;
  remindStartQuestionnaireEnabledAt: string | null;
  remindBookAppointmentEnabledAt: string | null;
  disabledNotifications: Array<{}> | null;
  displayPicture: string | null;
};

export type W_Onboarding = {
  name: string | null;
  phone: string | null;
  gender: string | null;
  dob: string | null;
  ethnicity: string | null;
  country: string | null;
  height: number | null;
  weight: number | null;
  consentMarketing: boolean | null;
  pregnancyIntent: string | null;
  lastPeriodStartedAt: string | null;
  generalHealthConditions: Array<{}> | null;
  recipePreferences: Array<string>;
  observedSymptomForConditions: Array<{}> | null;
  diagnoses: Array<{
    type: string;
    diagnosedBy: string;
    diagnosedAge: number;
    managements: Array<string>;
  }>;
  onboardingSymptoms: Array<{
    symptom: string;
    severity: number;
    duration: string;
  }>;
  symptomsAfterMeal: Array<string>;
  symptomsAroundPeriod: Array<string>;
  contraceptions: Array<{
    type: string;
    currentlyUsing: boolean;
    usageLength: number;
    stoppedUsingAt: string | null;
    customSymptom: string | null;
    symptoms: Array<string>;
  }>;
};

export type W_Register = {
  email: string;
  plainPassword: string;
  timezone: string;
  isPartnerProgram: boolean | null;
};

export type W_UpdateAsAdmin = {
  email: string;
  weight: number | null;
  generalHealthConditions: Array<{}> | null;
  recipePreferences: Array<string>;
  diagnoses: Array<{
    type: string;
    diagnosedBy: string;
    diagnosedAge: number;
    managements: Array<string>;
  }>;
  frequentSymptoms: Array<string>;
  contraceptions: Array<{
    type: string;
    currentlyUsing: boolean;
    usageLength: number;
    stoppedUsingAt: string | null;
    customSymptom: string | null;
    symptoms: Array<string>;
  }>;
  classifications: Array<string>;
  contentTags: Array<string>;
};

export type W_UpdateEmail = {
  email: string;
};

export function useReadCollectionClinicians(
  queryParams?: {
    activeSubscription?: string;
    classifications?: string;
    'classifications[]'?: Array<string>;
    'clinicianProfile.isActive'?: boolean;
    'clinicianProfile.type'?: string;
    'clinicianProfile.type[]'?: Array<string>;
    coach?: string;
    'coach[]'?: Array<string>;
    organisationTeam?: string;
    physician?: string;
    'physician[]'?: Array<string>;
    programStatus?: string;
    search?: string;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ClinicianListAsAdmin>>(
    `/clinicians${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadCollection(
  queryParams?: {
    classifications?: string;
    'classifications[]'?: Array<string>;
    'clinicianProfile.isActive'?: boolean;
    'clinicianProfile.type'?: string;
    'clinicianProfile.type[]'?: Array<string>;
    coach?: string;
    'coach[]'?: Array<string>;
    organisationTeam?: string;
    physician?: string;
    'physician[]'?: Array<string>;
    plan?: string; // four_month | twelve_month
    programStatus?: string;
    programStatusProgram?: string; // program number; the latest program when absent
    search?: string;
    subSource?: string;
    subStatus?: string;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ListAsAdmin>>(
    `/users${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadCollectionClients(
  id: string,
  queryParams?: {
    activeSubscription?: string;
    classifications?: string;
    'classifications[]'?: Array<string>;
    'clinicianProfile.isActive'?: boolean;
    'clinicianProfile.type'?: string;
    'clinicianProfile.type[]'?: Array<string>;
    coach?: string;
    'coach[]'?: Array<string>;
    organisationTeam?: string;
    physician?: string;
    'physician[]'?: Array<string>;
    programStatus?: string;
    search?: string;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ClientList>>(
    `/clinicians/${id}/clients${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadItem(id: string, options?: ReadConfig) {
  return useApiRead<V_ClinicianDetailAsAdmin>(`/clinicians/${id}`, options);
}
